import { useToast, breakpoints } from '@chillibean/component-library';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import { ActivitiesNavigationHeader, ActivitiesTableHeader } from '../components/container';
import { FilterModal, ExportSuccessModal, ACCORDION_TYPES } from '../components/modals';
import { ActivitiesTable } from '../components/presentational';
import { useFilterChips, useFilterParams, useMounted, useExportActivities, useFilterModalValues } from '../hooks';
import {
  fetchFilteringActions,
  fetchFilteringLocations,
  fetchFilteringPerformers,
  fetchActivities,
  setActivitiesFilters,
} from '../redux/actions';

function ActivitiesPage() {
  const { account } = useSelector((state) => state.internalReducer.internal);
  const [showExportSuccessModal, setShowExportSuccessModal] = useState(false);
  const dispatch = useDispatch();
  const { handleClearFilters, handleUpdateValues, values } = useFilterModalValues();
  const {
    activitiesCurrentPage: { data: currentPage },
    activitiesFilters: { data: filters },
    activitiesPerPage: { data: perPage },
  } = useSelector((state) => state.activitiesReducer);
  const { mounted } = useMounted({
    requiredValues: [account, currentPage, perPage, filters],
  });
  const {
    filteringActions: { data: filterActions },
    filteringLocations: { data: filterLocations },
    filteringPerformers: { data: filterPerformers },
  } = useSelector((state) => state.filteringReducer);
  const filterDefinitionsReady =
    Boolean(filterActions.length) && Boolean(filterLocations.length) && Boolean(filterPerformers.length);
  const { updateSearchParam } = useFilterParams();
  const { formatFilterChips } = useFilterChips();
  const DEFAULT_FILTER_MODAL = { section: ACCORDION_TYPES.CRITERIA, visible: false };
  const [filterModal, setFilterModal] = useState(DEFAULT_FILTER_MODAL);
  const { exportActivities } = useExportActivities();
  const { showErrorToast } = useToast();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramsObject = Array.from(params.entries()).reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});
    dispatch(setActivitiesFilters({ data: paramsObject }));
  }, [window.location.search]);

  useEffect(() => {
    if (mounted) {
      dispatch(fetchFilteringActions());
      dispatch(fetchFilteringLocations());
      dispatch(fetchFilteringPerformers());
    }
  }, [mounted]);

  useEffect(() => {
    if (filterDefinitionsReady) {
      formatFilterChips(filters);
    }
  }, [filters, filterDefinitionsReady]);

  useEffect(() => {
    if (mounted) {
      dispatch(fetchActivities({ filters: filters, page: currentPage, perPage: perPage }));
    }
  }, [mounted, filters]);

  useEffect(() => {
    updateSearchParam('page', currentPage);
    updateSearchParam('per_page', perPage);
  }, []);

  const handleExportActivities = () => {
    exportActivities({ onError: (e) => showErrorToast(e), onSuccess: () => setShowExportSuccessModal(true) });
  };

  const handleOpenFilterModal = (sectionType = ACCORDION_TYPES.CRITERIA) => {
    setFilterModal({ section: sectionType, visible: true });
  };

  const handleCloseFilterModal = () => {
    setFilterModal(DEFAULT_FILTER_MODAL);
  };

  return (
    <>
      <FilterModal
        defaultAccordionSection={filterModal.section}
        onClose={handleCloseFilterModal}
        updateValues={handleUpdateValues}
        values={values}
        visible={filterModal.visible}
      />
      <ExportSuccessModal onClose={() => setShowExportSuccessModal(false)} visible={showExportSuccessModal} />
      <ActivitiesNavigationHeader />
      <ActivitiesContainer>
        <ActivitiesTableHeader onExport={handleExportActivities} toggleShowFilterModal={handleOpenFilterModal} />
        <ActivitiesTable clearFilters={handleClearFilters} />
      </ActivitiesContainer>
    </>
  );
}

const ActivitiesContainer = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 178px);

  @media (width <= ${breakpoints.mobile}) {
    height: calc(100vh - 250px);
  }
`;

export default ActivitiesPage;
